import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  useMediaQuery,
  Collapse,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListSubheader,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomLabel from "./CustomLabel";
import PinAccessDialog from "./PinAccessDialog";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COLORS } from "../assets/theme";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { getCachedPinAccess, subscribeToPinAccess } from "../utils/pinAccessCache";
import { useFeatures } from "../contexts/FeaturesContext";
import { useUser } from "../contexts/UserContext";
import { hasPermission } from "../utils/permissions";

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  to?: string;
  children?: MenuItem[];
  /**
   * Feature switch this entry belongs to. Children inherit it. When the switch
   * is off the entry stays visible but opens the upsell dialog instead of
   * navigating — that was the point of leaving it in the sidebar.
   */
  feature?: string;
  /**
   * Permission key the entry needs. Unlike a feature switch there is nothing to
   * upsell, so the entry is hidden when the user lacks it. ADMIN always passes.
   */
  permission?: string;
}

interface SidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  sidebarCollapsed: boolean;
  onSidebarToggle: () => void;
  drawerWidth: number;
  collapsedWidth: number;
  menuItems: MenuItem[];
  venueName: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  onDrawerToggle,
  sidebarCollapsed,
  onSidebarToggle,
  drawerWidth,
  collapsedWidth,
  menuItems: allMenuItems,
  venueName,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const { isEnabled, showFeatureLocked } = useFeatures();
  const { user } = useUser();
  const menuItems = useMemo(() => {
    const visible = (items: MenuItem[]): MenuItem[] =>
      items
        .filter((item) => !item.permission || hasPermission(item.permission, user))
        .map((item) =>
          item.children ? { ...item, children: visible(item.children) } : item
        );
    return visible(allMenuItems);
  }, [allMenuItems, user]);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  );
  const financeMenuLabel = "Finance";
  const inventoryMenuLabel = "Inventory";
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingPinAction, setPendingPinAction] = useState<{
    type: "expand" | "navigate";
    label?: string;
    path?: string;
    // Set when the request came from the collapsed sidebar: open the flyout
    // here instead of expanding the (hidden) group.
    anchor?: HTMLElement;
  } | null>(null);
  // Collapsed desktop sidebar can't show children inline, so groups open as a
  // menu beside the rail instead.
  const [flyout, setFlyout] = useState<{
    anchor: HTMLElement;
    item: MenuItem;
    feature?: string;
  } | null>(null);
  const normalizePath = (path: string) => {
    const trimmed = path.replace(/\/+$/, "");
    return trimmed.length ? trimmed : "/";
  };
  const menuPaths = useMemo(() => {
    const paths: string[] = [];
    const collectPaths = (items: MenuItem[]) => {
      items.forEach((menuItem) => {
        if (menuItem.to) {
          paths.push(normalizePath(menuItem.to));
        }
        if (menuItem.children) {
          collectPaths(menuItem.children);
        }
      });
    };
    collectPaths(menuItems);
    return paths;
  }, [menuItems]);
  const normalizedCurrentPath = normalizePath(location.pathname);
  const isFinanceRoute = normalizedCurrentPath.includes("/finance/");

  useEffect(() => {
    const unsubscribe = subscribeToPinAccess((granted) => {
      if (!granted) {
        setExpandedItems((prev) =>
          prev[financeMenuLabel] || prev[inventoryMenuLabel]
            ? {
                ...prev,
                [financeMenuLabel]: false,
                [inventoryMenuLabel]: false,
              }
            : prev
        );
      }
    });
    return unsubscribe;
  }, [financeMenuLabel, inventoryMenuLabel]);

  const handleOpenFinancePinDialog = () => {
    if (pinDialogOpen) return;
    setPinDialogOpen(true);
  };

  const handleCloseFinancePinDialog = () => {
    setPinDialogOpen(false);
    setPendingPinAction(null);
  };

  const handleFinancePinVerified = () => {
    setPinDialogOpen(false);
    if (pendingPinAction?.type === "expand" && pendingPinAction.label) {
      const target = menuItems.find((m) => m.label === pendingPinAction.label);
      if (pendingPinAction.anchor && target) {
        setFlyout({
          anchor: pendingPinAction.anchor,
          item: target,
          feature: target.feature,
        });
      } else {
        handleToggleExpand(pendingPinAction.label);
      }
    } else if (pendingPinAction?.type === "navigate" && pendingPinAction.path) {
      navigate(pendingPinAction.path);
    } else {
      handleToggleExpand(financeMenuLabel);
    }
    setPendingPinAction(null);
  };

  const isPinProtectedPath = (path: string) => {
    const normalizedPath = normalizePath(path);
    if (normalizedPath.includes("/finance/")) return true;
    if (/\/stock($|\/)/.test(normalizedPath)) return true;
    if (/\/purchases($|\/)/.test(normalizedPath)) return true;
    if (/\/products($|\/)/.test(normalizedPath)) return true;
    return false;
  };

  const handleToggleExpand = (itemLabel: string) => {
    setExpandedItems((prev) => {
      const isCurrentlyExpanded = prev[itemLabel];

      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [itemLabel]: false,
        };
      } else {
        const newState: Record<string, boolean> = {};
        Object.keys(prev).forEach((key) => {
          newState[key] = false;
        });
        newState[itemLabel] = true;
        return newState;
      }
    });
  };

  const handleParentItemClick = (
    item: MenuItem,
    locked: boolean,
    anchor: HTMLElement,
    feature?: string
  ) => {
    const asFlyout = sidebarCollapsed && isSmUp;
    // A locked section has nothing behind it, so don't ask for a PIN to open it.
    if (
      !locked &&
      (item.label === financeMenuLabel || item.label === inventoryMenuLabel)
    ) {
      const cachedAccess = getCachedPinAccess();
      if (!cachedAccess) {
        setPendingPinAction({
          type: "expand",
          label: item.label,
          anchor: asFlyout ? anchor : undefined,
        });
        handleOpenFinancePinDialog();
        return;
      }
    }
    if (asFlyout) {
      setFlyout({ anchor, item, feature });
      return;
    }
    handleToggleExpand(item.label);
  };

  // Same rules as clicking a leaf in the expanded sidebar.
  const handleFlyoutChildClick = (child: MenuItem) => {
    const feature = child.feature || flyout?.feature;
    setFlyout(null);
    if (feature && !isEnabled(feature)) {
      showFeatureLocked(feature);
      return;
    }
    if (!child.to) return;
    if (isPinProtectedPath(child.to) && !getCachedPinAccess()) {
      setPendingPinAction({ type: "navigate", path: child.to });
      handleOpenFinancePinDialog();
      return;
    }
    navigate(child.to);
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.to) {
      const normalizedItemPath = normalizePath(item.to);
      const hasDescendantPath = menuPaths.some((path) => {
        if (path === normalizedItemPath) {
          return false;
        }
        return path.startsWith(`${normalizedItemPath}/`);
      });

      if (normalizedCurrentPath === normalizedItemPath) {
        return true;
      }

      if (
        !hasDescendantPath &&
        normalizedCurrentPath.startsWith(`${normalizedItemPath}/`)
      ) {
        return true;
      }
    }

    if (item.children) {
      return item.children.some((child) => isItemActive(child));
    }

    return false;
  };

  // Open the group holding the current page, e.g. after a refresh on
  // /finance/invoices. Keyed on the group so a manual collapse sticks while
  // moving between its own pages; PIN groups stay shut until access is cached.
  const activeParentLabel = menuItems.find((item) =>
    item.children?.some((child) => isItemActive(child))
  )?.label;
  useEffect(() => {
    if (!activeParentLabel) return;
    const pinGroup =
      activeParentLabel === financeMenuLabel ||
      activeParentLabel === inventoryMenuLabel;
    if (pinGroup && !getCachedPinAccess()) return;
    setExpandedItems((prev) =>
      prev[activeParentLabel] ? prev : { [activeParentLabel]: true }
    );
  }, [activeParentLabel]);

  const renderMenuItem = (
    item: MenuItem,
    level: number = 0,
    inheritedFeature?: string
  ) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.label];
    const itemIsActive = isItemActive(item);
    const feature = item.feature || inheritedFeature;
    const locked = !!feature && !isEnabled(feature);
    const pinProtected =
      item.to && !hasChildren && !locked ? isPinProtectedPath(item.to) : false;
    const railMode = sidebarCollapsed && isSmUp;
    // On an open group the highlighted child already shows where you are.
    const showActiveBar =
      itemIsActive && (!hasChildren || !isExpanded || railMode);

    return (
      <React.Fragment key={item.label}>
        <ListItem disablePadding sx={{ mb: { xs: 1, sm: 0.5 } }}>
          <Tooltip
            title={sidebarCollapsed && isSmUp ? item.label : ""}
            placement="right"
          >
            <ListItemButton
              component={hasChildren ? "div" : Link}
              to={hasChildren ? undefined : item.to}
              onClick={
                hasChildren
                  ? (event: React.MouseEvent<HTMLDivElement>) =>
                      handleParentItemClick(
                        item,
                        locked,
                        event.currentTarget,
                        feature
                      )
                  : (event: React.MouseEvent<HTMLDivElement>) => {
                      if (locked && feature) {
                        event.preventDefault();
                        showFeatureLocked(feature);
                        return;
                      }
                      if (pinProtected && !getCachedPinAccess()) {
                        event.preventDefault();
                        setPendingPinAction({
                          type: "navigate",
                          path: item.to,
                        });
                        handleOpenFinancePinDialog();
                      }
                    }
              }
              sx={{
                borderRadius: 2,
                bgcolor: itemIsActive ? COLORS.SidebarActiveBg : "transparent",
                color: itemIsActive
                  ? COLORS.SidebarActiveText
                  : COLORS.SidebarInactiveText,
                px: sidebarCollapsed && isSmUp ? 1.5 : 2 + level * 0.5,
                py: { xs: 1.2, sm: 0.7 },
                justifyContent:
                  sidebarCollapsed && isSmUp ? "center" : "flex-start",
                minHeight: { xs: 42, sm: 32 },
                opacity: locked ? 0.55 : 1,
                transition: "all 0.2s",
                ml: level > 0 ? 1.5 : 0,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "22%",
                  bottom: "22%",
                  width: 3,
                  borderRadius: 3,
                  bgcolor: COLORS.SidebarActiveText,
                  transform: showActiveBar ? "scaleY(1)" : "scaleY(0)",
                  transition: "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
                },
                "&:hover": {
                  bgcolor: itemIsActive
                    ? COLORS.SidebarActiveBg
                    : "rgba(0, 0, 0, 0.04)",
                },
                "& > .MuiSvgIcon-root": {
                  transition: "transform 0.2s",
                },
                "&:hover > .MuiSvgIcon-root": {
                  transform: "translateX(2px)",
                },
                "& .MuiSvgIcon-root": {
                  fontSize:
                    sidebarCollapsed && isSmUp
                      ? "1.5rem"
                      : { xs: "1.4rem", sm: "1.1rem" },
                },
              }}
            >
              {item.icon}
              {(!sidebarCollapsed || !isSmUp) && (
                <>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      ml: 1.5,
                      fontWeight: 600,
                      fontSize:
                        level > 0
                          ? { xs: "0.95rem", sm: "0.8rem" }
                          : { xs: "1.05rem", sm: "0.9rem" },
                      color: itemIsActive
                        ? COLORS.SidebarActiveText
                        : COLORS.SidebarInactiveText,
                    }}
                  />
                  {locked && (
                    <Box
                      sx={{
                        ml: "auto",
                        display: "flex",
                        alignItems: "center",
                        color: COLORS.placeHolderColor,
                      }}
                    >
                      <LockOutlinedIcon sx={{ fontSize: "1rem !important" }} />
                    </Box>
                  )}
                  {hasChildren && (
                    <Box
                      sx={{
                        ml: locked ? 0.5 : "auto",
                        display: "flex",
                        "& .MuiSvgIcon-root": {
                          transition: "transform 0.25s cubic-bezier(0.2, 0, 0, 1)",
                          transform: isExpanded ? "rotate(180deg)" : "none",
                        },
                      }}
                    >
                      <ExpandMoreIcon />
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {hasChildren && (!sidebarCollapsed || !isSmUp) && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) =>
                renderMenuItem(child, level + 1, feature)
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        bgcolor: COLORS.WhiteBackground,
        color: COLORS.MainColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: { xs: 2, sm: 1.5 },
        mt: { xs: 6, sm: 6 },
      }}
    >
      <List sx={{ width: "100%" }}>
        <ListItem disablePadding sx={{ mb: { xs: 1, sm: 0.5 } }}>
          {sidebarCollapsed && isSmUp ? (
            <ListItemButton
              onClick={onSidebarToggle}
              sx={{
                borderRadius: 2,
                bgcolor: "transparent",
                color: COLORS.MainColor,
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 0.7 },
                justifyContent: "center",
                minHeight: { xs: 25, sm: 20 },
                maxHeight: { xs: 35, sm: 28 },
                transition: "all 0.2s",
                mt: { xs: 0, sm: 0.5 },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: "1.5rem", sm: "1.5rem" },
                },
              }}
            >
              <MenuIcon />
            </ListItemButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 1 },
                minHeight: { xs: 25, sm: 20 },
                maxHeight: { xs: 35, sm: 28 },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <IconButton
                  onClick={onSidebarToggle}
                  sx={{
                    color: COLORS.MainColor,
                    p: 0,
                    mr: 2,
                    minWidth: "auto",
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <Tooltip title={venueName || ""} placement="right">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomLabel
                    text={venueName}
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: COLORS.MainColor,
                      fontFamily: "inherit",
                      width: "80%",
                      overflow: "hidden",
                      overflowX: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}
        </ListItem>
        {menuItems.map((item) => renderMenuItem(item))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: sidebarCollapsed ? collapsedWidth : drawerWidth },
        flexShrink: { sm: 0 },
      }}
      aria-label="sidebar"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            bgcolor: COLORS.WhiteBackground,
            color: COLORS.MainColor,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: sidebarCollapsed ? collapsedWidth : drawerWidth,
            bgcolor: COLORS.WhiteBackground,
            color: COLORS.MainColor,
            transition: "width 0.2s",
            borderRight: "1px solid #eee",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      <Menu
        anchorEl={flyout?.anchor}
        open={!!flyout}
        onClose={() => setFlyout(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { ml: 1, minWidth: 200, borderRadius: 2 } },
        }}
      >
        {flyout && (
          <ListSubheader
            sx={{
              lineHeight: "32px",
              fontWeight: 700,
              color: COLORS.SidebarActiveText,
            }}
          >
            {flyout.item.label}
          </ListSubheader>
        )}
        {flyout?.item.children?.map((child) => {
          const childFeature = child.feature || flyout.feature;
          const childLocked = !!childFeature && !isEnabled(childFeature);
          const childActive = isItemActive(child);
          return (
            <MuiMenuItem
              key={child.label}
              selected={childActive}
              onClick={() => handleFlyoutChildClick(child)}
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: childActive
                  ? COLORS.SidebarActiveText
                  : COLORS.SidebarInactiveText,
                opacity: childLocked ? 0.55 : 1,
              }}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: 32,
                  "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
                }}
              >
                {child.icon}
              </ListItemIcon>
              {child.label}
              {childLocked && (
                <LockOutlinedIcon
                  sx={{
                    ml: "auto",
                    pl: 1,
                    fontSize: "1rem",
                    color: COLORS.placeHolderColor,
                  }}
                />
              )}
            </MuiMenuItem>
          );
        })}
      </Menu>
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={handleCloseFinancePinDialog}
        onVerified={handleFinancePinVerified}
      />
    </Box>
  );
};

export default Sidebar;
